import React, { useState, useEffect } from 'react';
import { Calendar } from 'primereact/calendar';
import { Button } from 'primereact/button';
import { Dropdown } from 'primereact/dropdown';
import { InputText } from 'primereact/inputtext';
import { toast } from 'react-toastify';
import globalSettingsBranches from '../../../stores/global-settings-branches';
import globalSettingsStores from '../../../stores/settings-stores';
import globalConfigStore from '../../../stores/global-config-store';
import { getHebrewYear } from '../../../utils/hebrew-date-utils';
import { FilterExpanderWithListOfCheckboxes } from '../../../components/CustomExpander/componenets/filter-expander-with-list-of-checkboxes';
import { CustomExpander } from '../../../components/CustomExpander/CustomExpander';
import { CourseStatus } from '../../../api/types/programs/course/course-status';
import appApi from '../../../api/app-api';
import './courses-filter.css';
import { observer } from 'mobx-react-lite';
import { FindUser } from '../../find-user-page/find-user';
import { UserType } from '../../../api/types/user/user-type';
import { UserSummaryType } from '../../../api/types/user/user-summary.type';

interface CoursesFilterProps {
  onFilterChange?: (filters: CourseFilters) => void;
}

export interface CourseFilters {
  branchIds: number[];
  abstractProgramIds: number[];
  programStartYears: number[];
  programYears: number[];
  courseStartDateFrom?: Date;
  courseStartDateTo?: Date;
  courseStatus: CourseStatus[];
  majorIds: number[];
  abstractCourseIds: number[];
  lecturerIds: number[];
  additionalCourseName?: string;
}

const PROGRAM_YEARS = [
  { label: 'שנה א׳', value: '0' },
  { label: 'שנה ב׳', value: '1' },
  { label: 'שנה ג׳', value: '2' },
  { label: 'שנה ד׳', value: '3' },
  { label: 'שנה ה׳', value: '4' },
];

export const CoursesFilter: React.FC<CoursesFilterProps> = observer(
  ({ onFilterChange }) => {
    const currentYear = new Date().getFullYear();
    const globalYear = globalConfigStore.rootFilter.currentYear;
    const defaultYear = globalYear ?? currentYear;

    const years = Array.from({ length: currentYear - 2000 + 4 }, (_, i) => ({
      label: `${getHebrewYear(currentYear + 3 - i)} (${currentYear + 3 - i})`,
      value: (currentYear + 3 - i).toString(),
    }));

    const [filters, setFilters] = useState<CourseFilters>({
      branchIds: [],
      abstractProgramIds: [],
      programStartYears: [defaultYear],
      programYears: [],
      courseStatus: [],
      majorIds: [],
      abstractCourseIds: [],
      lecturerIds: [],
      additionalCourseName: '',
      courseStartDateFrom: undefined,
      courseStartDateTo: undefined,
    });

    // Sync programStartYears when globalConfigStore.rootFilter.currentYear changes
    useEffect(() => {
      if (globalYear !== undefined) {
        setFilters((prev) => ({
          ...prev,
          programStartYears: [globalYear],
        }));
      }
    }, [globalYear]);

    const [selectedLecturer, setSelectedLecturer] = useState<
      UserSummaryType | undefined
    >();

    const handleFilterChange = (newFilters: Partial<CourseFilters>) => {
      const updatedFilters = { ...filters, ...newFilters };
      setFilters(updatedFilters);
      /*onFilterChange?.(updatedFilters);*/
    };

    const handleProgramToggle = (programId: number) => {
      const newProgramIds = filters.abstractProgramIds.includes(programId)
        ? filters.abstractProgramIds.filter((id) => id !== programId)
        : [...filters.abstractProgramIds, programId];
      handleFilterChange({ abstractProgramIds: newProgramIds });
    };

    const handleStartYearToggle = (year: string) => {
      const yearNum = parseInt(year);
      const newYears = filters.programStartYears.includes(yearNum)
        ? filters.programStartYears.filter((y) => y !== yearNum)
        : [...filters.programStartYears, yearNum];
      handleFilterChange({ programStartYears: newYears });
    };

    const handleProgramYearToggle = (year: string) => {
      const yearNum = parseInt(year);
      const newYears = filters.programYears.includes(yearNum)
        ? filters.programYears.filter((y) => y !== yearNum)
        : [...filters.programYears, yearNum];
      handleFilterChange({ programYears: newYears });
    };

    const handleStatusToggle = (status: CourseStatus) => {
      const newStatusIds = filters.courseStatus.includes(status)
        ? filters.courseStatus.filter((id) => id !== status)
        : [...filters.courseStatus, status];
      handleFilterChange({ courseStatus: newStatusIds });
    };

    const handleMajorToggle = (majorId: number) => {
      const newMajorIds = filters.majorIds.includes(majorId)
        ? filters.majorIds.filter((id) => id !== majorId)
        : [...filters.majorIds, majorId];
      handleFilterChange({ majorIds: newMajorIds });
    };

    const handleCourseToggle = (courseId: number) => {
      const newCourseIds = filters.abstractCourseIds.includes(courseId)
        ? filters.abstractCourseIds.filter((id) => id !== courseId)
        : [...filters.abstractCourseIds, courseId];
      handleFilterChange({ abstractCourseIds: newCourseIds });
    };

    const handleLecturerSelection = (lecturer: UserSummaryType | undefined) => {
      setSelectedLecturer(lecturer);
      handleFilterChange({
        lecturerIds: lecturer ? [lecturer.id] : [],
      });
    };

    const handleShowFilters = async () => {
      try {
        if (onFilterChange) {
          onFilterChange({
            branchIds: filters.branchIds,
            abstractProgramIds: filters.abstractProgramIds,
            programStartYears: filters.programStartYears,
            programYears: filters.programYears,
            courseStartDateFrom: filters.courseStartDateFrom,
            courseStartDateTo: filters.courseStartDateTo,
            courseStatus: filters.courseStatus,
            majorIds: filters.majorIds,
            abstractCourseIds: filters.abstractCourseIds,
            lecturerIds: filters.lecturerIds,
            ...(filters.additionalCourseName?.trim().length &&
            filters.additionalCourseName.trim().length >= 3
              ? { additionalCourseName: filters.additionalCourseName.trim() }
              : {}),
          });
        }
      } catch (error) {
        console.error('Error applying filters:', error);
        toast.error('שגיאה בהחלת הפילטרים');
      }
    };

    const handleClearAllFilters = () => {
      setFilters({
        branchIds: [],
        abstractProgramIds: [],
        programStartYears: [],
        programYears: [],
        courseStatus: [],
        majorIds: [],
        abstractCourseIds: [],
        lecturerIds: [],
        additionalCourseName: '',
        courseStartDateFrom: undefined,
        courseStartDateTo: undefined,
      });
      setSelectedLecturer(undefined);
    };

    return (
      <div
        style={{
          height: 'calc(100vh - 72px)',
        }}
        className="side-bar-filter"
      >
        <div
          className="filter-header"
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: '8px',
          }}
        >
          <span>סינונים</span>
          <Button
            icon="pi pi-filter-slash"
            text
            severity="secondary"
            tooltip={'נקה הכל'}
            onClick={handleClearAllFilters}
          />
        </div>
        <div className="filter-content">
          <CustomExpander>
            <div>שלוחה</div>
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                gap: '8px',
                padding: '8px',
              }}
            >
              <Dropdown
                value={filters.branchIds[0] ?? null}
                onChange={(e) =>
                  handleFilterChange({
                    branchIds: e.value ? [e.value] : [],
                  })
                }
                options={globalSettingsBranches.branches}
                optionLabel="branchName"
                optionValue="id"
                placeholder="בחר שלוחה"
                style={{ width: '100%' }}
              />
            </div>
          </CustomExpander>

          <CustomExpander>
            <div>חיפוש לפי מרצה</div>
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                gap: '8px',
                padding: '8px',
              }}
            >
              <FindUser
                displayTable={false}
                onApprove={() => {
                  //
                }}
                onUserSelected={handleLecturerSelection}
                multiple={false}
                role={UserType.LECTURER}
                displayApprove={false}
              />
              {selectedLecturer && (
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    padding: '8px',
                    backgroundColor: '#f0f0f0',
                    borderRadius: '4px',
                    marginTop: '8px',
                  }}
                >
                  <span>
                    {selectedLecturer.firstName} {selectedLecturer.lastName}
                  </span>
                  <Button
                    icon="pi pi-times"
                    className="p-button-rounded p-button-text p-button-sm"
                    onClick={() => handleLecturerSelection(undefined)}
                    tooltip="הסר מרצה"
                  />
                </div>
              )}
            </div>
          </CustomExpander>

          <FilterExpanderWithListOfCheckboxes
            title="קורס טיפוסי"
            items={globalSettingsStores.abstractCourses}
            selectedItems={filters.abstractCourseIds}
            onItemToggle={handleCourseToggle}
            getItemLabel={(course) => course.courseName}
            getItemKey={(course) => course.id}
            searchPlaceholder="חפש קורס..."
          />

          <CustomExpander>
            <div>שם נוסף</div>
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                gap: '8px',
                padding: '8px',
              }}
            >
              <InputText
                value={filters.additionalCourseName}
                onChange={(e) =>
                  handleFilterChange({ additionalCourseName: e.target.value })
                }
                placeholder="הקלד לפחות 3 תווים"
                style={{ width: '100%' }}
              />
            </div>
          </CustomExpander>

          <FilterExpanderWithListOfCheckboxes
            title="מגמה"
            items={globalSettingsStores.majors}
            selectedItems={filters.majorIds}
            onItemToggle={handleMajorToggle}
            getItemLabel={(major) => major.majorName}
            getItemKey={(major) => major.id}
            searchPlaceholder="חפש מגמה..."
          />

          <FilterExpanderWithListOfCheckboxes
            title="תוכנית לימודים"
            items={globalSettingsStores.abstractProgram}
            selectedItems={filters.abstractProgramIds}
            onItemToggle={handleProgramToggle}
            getItemLabel={(program) => program.programName}
            getItemKey={(program) => program.id}
            searchPlaceholder="חפש תוכנית..."
          />

          <FilterExpanderWithListOfCheckboxes
            title="שנת התחלה"
            items={years}
            selectedItems={filters.programStartYears.map(String)}
            onItemToggle={handleStartYearToggle}
            getItemLabel={(year) => year.label}
            getItemKey={(year) => year.value}
            searchPlaceholder="חפש שנה..."
          />

          <FilterExpanderWithListOfCheckboxes
            title="שנה בתוכנית"
            items={PROGRAM_YEARS}
            selectedItems={filters.programYears.map(String)}
            onItemToggle={handleProgramYearToggle}
            getItemLabel={(year) => year.label}
            getItemKey={(year) => year.value}
            searchPlaceholder="חפש שנה..."
          />

          <CustomExpander>
            <div>תאריך התחלה</div>
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                gap: '8px',
                padding: '8px',
              }}
            >
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '8px',
                }}
              >
                <label style={{ fontSize: '0.9rem', color: '#666' }}>
                  מתאריך
                </label>
                <Calendar
                  value={filters.courseStartDateFrom}
                  onChange={(e) =>
                    handleFilterChange({
                      courseStartDateFrom: e.value as Date,
                    })
                  }
                  dateFormat="dd/mm/yy"
                  showIcon
                  iconPos="right"
                  style={{
                    height: '36px',
                    width: '100%',
                    direction: 'ltr',
                  }}
                  inputStyle={{ direction: 'rtl' }}
                />
              </div>
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '8px',
                }}
              >
                <label style={{ fontSize: '0.9rem', color: '#666' }}>
                  עד תאריך
                </label>
                <Calendar
                  value={filters.courseStartDateTo}
                  onChange={(e) =>
                    handleFilterChange({ courseStartDateTo: e.value as Date })
                  }
                  dateFormat="dd/mm/yy"
                  showIcon
                  iconPos="right"
                  style={{
                    height: '36px',
                    width: '100%',
                    direction: 'ltr',
                  }}
                  inputStyle={{ direction: 'rtl' }}
                />
              </div>
            </div>
          </CustomExpander>

          <FilterExpanderWithListOfCheckboxes
            title="סטטוס קורס"
            items={[
              CourseStatus.INIT,
              CourseStatus.ACTIVE,
              CourseStatus.CLOSED,
              CourseStatus.FINISHED,
            ]}
            selectedItems={filters.courseStatus}
            onItemToggle={handleStatusToggle}
            getItemLabel={(status) => CourseStatus.getDisplayText(status)}
            getItemKey={(status) => status}
            searchPlaceholder="חפש סטטוס..."
          />

          <Button
            label="חפש"
            icon="pi pi-filter"
            onClick={handleShowFilters}
            className="filter-button p-button-outlined"
          />
        </div>
      </div>
    );
  },
);
